// Full-screen modal shell. Renders via React portal at document.body so the
// overlay sits above the host-page content regardless of where the widget is
// mounted. Three ways to close: backdrop click, the × button, or ESC.
//
// Designed for the post-mount-point pivot: instead of pushing host-page
// content down with an inline widget, every trigger element on the page
// opens this modal which contains the booking flow.

import { useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import styles from '../styles/widget.module.css';
import Close from '../assets/icons/Close';


interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional label for accessibility (announced when the modal opens). */
  ariaLabel?: string;
  /** Inline style merged onto the modal content shell (used by the widget
   *  to drive a measured + transitioned height across booking steps). */
  contentStyle?: CSSProperties;
  /** 'bare' = transparent overlay (no dark scrim) + near-full-screen content.
   *  Used by inline mode's mobile Steps 2-4 so the landing background stays
   *  visible in the thin margin around the modal. Defaults to 'default'. */
  variant?: 'default' | 'bare';
  /** When set, the content "grows" (FLIP) from this viewport rect on open
   *  instead of the default slide-up — used by the inline flow so Step 2
   *  expands out of the in-flow Step-1 card. */
  growFromRect?: { top: number; left: number; width: number; height: number } | null;
}

export function Modal({ isOpen, onClose, children, ariaLabel = 'Book a fitting', contentStyle, variant = 'default', growFromRect }: ModalProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  // Stable ref to onClose so the effect below isn't re-keyed when the
  // parent passes a new function identity (the Widget re-creates
  // handleCloseModal on every render). Without this, every keystroke
  // in an input triggers re-mount + focus-steal, kicking the cursor
  // out of whatever field the user is typing in.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Body-scroll lock + ESC listener + initial focus — runs ONLY on the
  // open transition, not on every parent re-render. Dependency is only
  // `isOpen`; onClose comes from the ref above.
  // Framed = running inside another page's iframe (the portal embedded on
  // the WordPress /account page). There the frame is sized to its content by
  // the embed height-reporter, so "the viewport" is the whole multi-thousand-
  // pixel frame: a body scroll lock doesn't stop any background scroll (the
  // background is the PARENT page, out of reach) — it only breaks scroll
  // chaining, wedging the parent page while the modal is open. And centering
  // against that frame puts the dialog nowhere near the user's eyes.
  const framed = typeof window !== 'undefined' && window.parent !== window;

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    const prevOverflow = document.body.style.overflow;
    if (!framed) document.body.style.overflow = 'hidden';
    if (framed) {
      // Ask the host page to bring the embed's top into view — the modal is
      // pinned there, and only the parent can scroll itself.
      window.parent.postMessage({ type: 'cc-embed:scroll-to-top' }, '*');
    }
    window.addEventListener('keydown', onKey);

    // Focus the modal container on open so screen readers announce the
    // dialog. tabIndex={-1} makes it focusable. Only steal focus on the
    // open transition — never on subsequent re-renders.
    requestAnimationFrame(() => contentRef.current?.focus());

    return () => {
      window.removeEventListener('keydown', onKey);
      if (!framed) document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, framed]);

  // FLIP grow-from-card. Runs before paint so we can suppress the default
  // slide-up keyframe and start the content collapsed onto the Step-1 card's
  // rect, then transition it up to its natural size/position. transform-only
  // (GPU) so it stays smooth; the box grows out of the card instead of wiping.
  useLayoutEffect(() => {
    if (!isOpen || !growFromRect) return;
    const el = contentRef.current;
    if (!el) return;
    const final = el.getBoundingClientRect();
    if (!final.width || !final.height) return;
    const dx = growFromRect.left - final.left;
    const dy = growFromRect.top - final.top;
    const sx = Math.max(0.08, growFromRect.width / final.width);
    const sy = Math.max(0.08, growFromRect.height / final.height);

    el.style.animation = 'none'; // cancel ccSlideUp
    el.style.transformOrigin = 'top left';
    el.style.willChange = 'transform';
    el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
    // Commit the collapsed start state before transitioning to identity.
    void el.getBoundingClientRect();
    el.style.transition = 'transform 440ms cubic-bezier(0.34, 0.03, 0.2, 1)';
    el.style.transform = 'translate(0px, 0px) scale(1, 1)';

    let done = false;
    const cleanup = () => {
      if (done) return;
      done = true;
      el.style.transition = '';
      el.style.transform = '';
      el.style.transformOrigin = '';
      el.style.willChange = '';
      el.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (e: TransitionEvent) => {
      if (e.propertyName === 'transform') cleanup();
    };
    el.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(cleanup, 520);
    return () => {
      window.clearTimeout(timer);
      cleanup();
    };
  }, [isOpen, growFromRect]);

  if (!isOpen) return null;

  // Framed geometry: anchor the dialog where the user actually is — the card
  // they tapped (growFromRect) — instead of centering in the giant frame, and
  // cap its height in real pixels since 100vh means "the whole frame" here.
  const framedOverlay: React.CSSProperties | undefined = framed
    ? {
        // Pinned to the top of the frame's document — the frame's top IS the
        // embed's top, and the host smooth-scrolls there on open (see the
        // scroll-to-top message below), so the dialog is fully visible in
        // the same position every time.
        position: 'absolute',
        alignItems: 'flex-start',
        paddingTop: 24,
      }
    : undefined;
  const framedContent: React.CSSProperties | undefined = framed
    ? { minHeight: 480, maxHeight: 760 }
    : undefined;

  return createPortal(
    <div
      className={variant === 'bare' ? styles.modalOverlayBare : styles.modalOverlay}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      style={framedOverlay}
      onClick={onClose}
    >
      <div
        className={styles.modalContent}
        ref={contentRef}
        tabIndex={-1}
        style={{ ...contentStyle, ...framedContent }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.modalClose}
          aria-label="Close booking widget"
          onClick={onClose}
        >
          <Close />
        </button>
        <div className={styles.modalContentInner}>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
